import { useState, useEffect } from 'react'
import { FileText, CaretDown, CaretUp, Check, Eye, X } from '@phosphor-icons/react'
import { Button } from '../ui/button'
import { useOfferConditions, OfferCondition } from '../../hooks/useOfferConditions'

interface OfferConditionsPanelProps {
  selectedConditionIds: string[]
  onConditionsChange: (conditionIds: string[]) => void
  // External conditions (e.g., from an existing offer being edited)
  // These will be merged with localStorage conditions
  externalConditions?: Array<{ id: string; title: string; content: string }>
}

export function OfferConditionsPanel({
  selectedConditionIds,
  onConditionsChange,
  externalConditions,
}: OfferConditionsPanelProps) {
  const { conditions: localStorageConditions, getDefaultConditions } = useOfferConditions()
  const [isExpanded, setIsExpanded] = useState(false)
  const [previewCondition, setPreviewCondition] = useState<OfferCondition | null>(null)

  // Merge localStorage conditions with external conditions (from offer being edited)
  // External conditions take precedence if there's an ID conflict
  const conditions = (() => {
    if (!externalConditions || externalConditions.length === 0) {
      return localStorageConditions
    }

    const externalIds = new Set(externalConditions.map(c => c.id))
    const localOnlyConditions = localStorageConditions.filter(c => !externalIds.has(c.id))

    // Map external conditions to OfferCondition format (add isDefault, createdAt, updatedAt)
    const externalAsOfferConditions: OfferCondition[] = externalConditions.map(c => ({
      ...c,
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }))

    return [...externalAsOfferConditions, ...localOnlyConditions]
  })()

  // Initialize with default conditions on mount (only for new offers, not edits)
  useEffect(() => {
    // Don't auto-select defaults if we have external conditions (editing mode)
    if (selectedConditionIds.length === 0 && localStorageConditions.length > 0 && !externalConditions) {
      const defaults = getDefaultConditions()
      if (defaults.length > 0) {
        onConditionsChange(defaults.map(c => c.id))
      }
    }
  }, [localStorageConditions]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCondition = (conditionId: string) => {
    if (selectedConditionIds.includes(conditionId)) {
      onConditionsChange(selectedConditionIds.filter(id => id !== conditionId))
    } else {
      onConditionsChange([...selectedConditionIds, conditionId])
    }
  }

  const selectedConditions = conditions.filter(c => selectedConditionIds.includes(c.id))

  if (conditions.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-500">
          <FileText size={18} />
          <span className="text-sm">
            Nenhuma condição cadastrada.{' '}
            <a href="/freight-forwarder/settings" className="text-emerald-600 hover:underline">
              Cadastre condições
            </a>
          </span>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg">
        {/* Header */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <FileText size={18} color="#3b82f6" weight="fill" />
            <span className="font-medium text-gray-900">Condições da Oferta</span>
            {selectedConditions.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {selectedConditions.length} selecionada{selectedConditions.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          {isExpanded ? (
            <CaretUp size={18} color="#6b7280" />
          ) : (
            <CaretDown size={18} color="#6b7280" />
          )}
        </button>

        {/* Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4 space-y-3">
            <p className="text-sm text-gray-500 mb-3">
              Selecione as condições que deseja incluir nesta oferta
            </p>

            {conditions.map((condition) => {
              const isSelected = selectedConditionIds.includes(condition.id)

              return (
                <div
                  key={condition.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleCondition(condition.id)}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}} // Controlled by onClick on parent
                    className="h-4 w-4 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900">{condition.title}</span>
                      {condition.isDefault && (
                        <span className="px-1.5 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">
                          Padrão
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                      {condition.content}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setPreviewCondition(condition)
                    }}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Ver condição completa"
                  >
                    <Eye size={16} />
                  </button>
                </div>
              )
            })}

            {/* Selected Summary */}
            {selectedConditions.length > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Condições selecionadas:
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedConditions.map((condition) => (
                    <span
                      key={condition.id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                    >
                      <Check size={12} />
                      {condition.title}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewCondition && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setPreviewCondition(null)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{previewCondition.title}</h3>
              <button
                onClick={() => setPreviewCondition(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {previewCondition.content}
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setPreviewCondition(null)}
              >
                Fechar
              </Button>
              <Button
                onClick={() => {
                  if (!selectedConditionIds.includes(previewCondition.id)) {
                    onConditionsChange([...selectedConditionIds, previewCondition.id])
                  }
                  setPreviewCondition(null)
                }}
                disabled={selectedConditionIds.includes(previewCondition.id)}
                style={{ backgroundColor: 'rgb(5, 150, 105)' }}
              >
                {selectedConditionIds.includes(previewCondition.id) ? (
                  <>
                    <Check size={16} className="mr-1" />
                    Selecionada
                  </>
                ) : (
                  'Selecionar'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// Helper component to display selected conditions (for offer preview/view)
interface OfferConditionsDisplayProps {
  conditionIds: string[]
}

export function OfferConditionsDisplay({ conditionIds }: OfferConditionsDisplayProps) {
  const { conditions } = useOfferConditions()
  const selectedConditions = conditions.filter(c => conditionIds.includes(c.id))

  if (selectedConditions.length === 0) {
    return null
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <FileText size={20} className="text-blue-500" />
        Condições da Oferta
      </h3>
      <div className="space-y-3">
        {selectedConditions.map((condition) => (
          <div
            key={condition.id}
            className="p-4 bg-gray-50 border border-gray-200 rounded-lg"
          >
            <h4 className="font-medium text-gray-900 mb-2">{condition.title}</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {condition.content}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
